/**
 * @file Expert dashboard UI — pages/Dashboard/Expert/Review/ExpertReviewPage.tsx.
 */

import React, { useEffect, useMemo, useState } from "react";
import axiosClient from "../../../../api/axiosClient";
import { API_URLS } from "../../../../constants/apiUrls";
import type { ExpertReviewSubmitStatus } from "../../../../constants/reviewStatus";
import SubmissionDetailLayout from "../../../../components/submission/SubmissionDetailLayout";
import "../../Admin/Materials/SubmissionDetail/AdminSubmissionDetail.css";
import "./ExpertReviewPage.css";

interface Props {
  submissionId: string;
  onBack?: () => void;
}

const ExpertReviewPage: React.FC<Props> = ({ submissionId, onBack }) => {
  const [submission, setSubmission] = useState<any>(null);
  const [history, setHistory] = useState<any[]>([]);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [subRes, histRes] = await Promise.all([
        axiosClient.get(`/submissions/${submissionId}`, {
          params: { includeFormFields: "true" },
        }),
        axiosClient.get(`/expert/review/history/${submissionId}`),
      ]);

      if (subRes.data?.success) setSubmission(subRes.data.data);
      if (histRes.data?.success) setHistory(histRes.data.data || []);
    } catch (err: any) {
      console.error("Load error", err);
      setError(err?.response?.data?.message || "Failed to load submission");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionId]);

  const extraReviewHistory = useMemo(() => history || [], [history]);

  const submitReview = async (status: ExpertReviewSubmitStatus) => {
    const label = status === "APPROVED" ? "APPROVED" : status === "FLAGGED_BY_EXPERT" ? "FLAGGED_BY_EXPERT" : status;
    const confirmed =
      typeof window === "undefined"
        ? true
        : window.confirm(`Submit expert review as ${label}?`);
    if (!confirmed) return;

    setSubmitting(true);
    try {
      const res = await axiosClient.post(API_URLS.EXPERT.REVIEW(submissionId), {
        status,
        comment: comment.trim() || undefined,
      });

      if (res.data?.success) {
        setComment("");
        await loadAll();
        alert("Review saved");
      } else {
        alert(res.data?.message || "Failed to save review");
      }
    } catch (err) {
      console.error("Submit review error", err);
      alert("Failed to save review");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="admin-loading">Loading submission...</div>;
  if (error) return <div className="admin-empty">Error: {error}</div>;
  if (!submission) return <div className="admin-empty">Submission not found.</div>;

  return (
    <SubmissionDetailLayout
      bannerTitle="Review Submission"
      submission={submission}
      onBack={() => onBack?.()}
      extraReviewHistory={extraReviewHistory}
    >
      {/* Add review / comment */}
      <div className="dataset-section erp-review-section">
        <h3 className="dataset-section-title">Add review / comment</h3>
        <textarea
          className="erp-comment-textarea"
          value={comment}
          onChange={(e) => setComment(e.target.value)}
          placeholder="Add comments for contributor or admin (optional)"
          aria-label="Review comment"
        />
        <div className="erp-actions">
          <button
            className="erp-btn erp-cancel"
            onClick={() => {
              if (!submitting) setComment("");
            }}
            disabled={submitting}
          >
            Clear
          </button>

          <button
            className="erp-btn erp-reject"
            type="button"
            onClick={() => submitReview("FLAGGED_BY_EXPERT")}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Flag / reject"}
          </button>

          <button
            className="erp-btn erp-primary"
            type="button"
            onClick={() => submitReview("APPROVED")}
            disabled={submitting}
          >
            {submitting ? "Saving…" : "Approve"}
          </button>
        </div>
      </div>
    </SubmissionDetailLayout>
  );
};

/** React component — Expert Review Page. */
export default ExpertReviewPage;
